import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  message,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Tabs,
  Table,
  Dropdown,
  Menu
} from "antd";
import { ChevronDown, Eye, Pen, Trash, TriangleAlert, Check } from "lucide-react";
import { DownOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatDate,
  formatNumber,
  formatString,
  formatStrings,
  getDateRange,
  handleChangePage,
  handlePageSizeChange,
  navigateTo,
} from "../../Utils/Common";
import CustomTable from "../../Components/Table/CustomTable";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { render } from "@testing-library/react";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const PaymentRequest = () => {
  const [loading, setLoading] = useState("");
  const [showModal, setShowModal] = useState("");
  const dateRange = getDateRange("month");
  const [filterConfig, setFilterConfig]: any = useState({
    date_from: formatDate(dateRange[0], "YYYY-MM-DD"),
    date_to: formatDate(dateRange[1], "YYYY-MM-DD"),
    page: 1,
    status: "pending",
  });
  const [dataSize, setDataSize] = useState(0);
  const [form] = Form.useForm();
  const [selectedRow, setSelectedRow]: any = useState({});
  const [dataSource, setDataSource] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");

  function clearFields() {
    setSelectedRow({});
    form.resetFields();
  }

  function onChangeTab(activeKey: any) {
    setActiveTab(activeKey);
    setFilterConfig({ page: 1, status: activeKey });
  }

  function getTableColumns() {
    switch (activeTab) {
      case "pending":
        return [
          {
            title: "Payment No.",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Amount",
            dataIndex: "grand_total",
            key: "grand_total",
            align: "right" as any,
            render: (value: any) => {
              const num = Number(value) || 0;
              const formatted = num.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              return formatted.replace(/\.$/, ""); // 👈 remove .00 if whole number
            },
          },
          {
            title: "Created By",
            dataIndex: "added_by_name",
            key: "added_by_name",
          },
          {
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => {
              const menu = (
                <Menu>
                  <Menu.Item
                    key="view"
                    icon={<Eye strokeWidth={1.5} size={15} />}
                    onClick={() =>
                      navigateTo(`/payment-request/view/${record.id}`)
                    }
                  >
                    View
                  </Menu.Item>
                  <Menu.Item
                    key="edit"
                    icon={<Pen strokeWidth={1.5} size={15} />}
                    onClick={() =>
                      navigateTo(`/payment-request/edit/${record.id}`)
                    }
                  >
                    Edit
                  </Menu.Item>
                  
                  <Menu.Item
                    key="edit"
                    icon={<Check strokeWidth={1.5} size={15} />}
                    onClick={() =>
                      navigateTo(`/payment-request/approval/${record.id}`)
                    }
                  >
                    Approve
                  </Menu.Item>
                  {/* <Menu.Item
                    key="delete"
                    icon={<Trash strokeWidth={1.5} size={15} />}
                    onClick={() => {
                      setSelectedRow(record);
                      setAction("delete");
                      setShowModal("confirm");
                    }}
                    danger
                  >
                    Delete
                  </Menu.Item> */}
                </Menu>
              );
              return (
                <Dropdown overlay={menu} trigger={["click"]}>
                  <Button>
                    Actions <ChevronDown strokeWidth={0.5} />
                  </Button>
                </Dropdown>
              );
            },
          },
        ];
      case "approved":
        return [
          {
            title: "Payment No.",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Amount",
            dataIndex: "grand_total",
            key: "grand_total",
            align: "right" as any,
            render: (value: any) => {
              const num = Number(value) || 0;
              const formatted = num.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              return formatted.replace(/\.$/, ""); // 👈 remove .00 if whole number
            },
          },
          {
            title: "Approved By",
            dataIndex: "approved_by_name",
            key: "approved_by_name",
          },
        ];
      case "disapproved":
        return [
          {
            title: "Payment No.",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Amount",
            dataIndex: "grand_total",
            key: "grand_total",
            align: "right" as any,
            render: (value: any) => {
              const num = Number(value) || 0;
              const formatted = num.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              return formatted.replace(/\.$/, ""); // 👈 remove .00 if whole number
            },
          },
          {
            title: "Disapproved By",
            dataIndex: "disapproved_by_name",
            key: "disapproved_by_name",
          },
        ];
      default:
        return [];
    }
  }

  function handleFilterChange(value: any, field: string) {
    if (field === "dates") {
      if (!value || value.length !== 2) {
        // Clear the date filters
        setFilterConfig((prev: any) => ({
          ...prev,
          date_from: null,
          date_to: null,
        }));
        return;
      }
      setFilterConfig((prev: any) => ({
        ...prev,
        date_from: value[0]?.format("YYYY-MM-DD"), // or "YYYY-MM" if you prefer
        date_to: value[1]?.format("YYYY-MM-DD"),
      }));
    } else {
      setFilterConfig((prev: any) => ({
        ...prev,
        [field]: value,
      }));
    }
  }

  async function submitForm(data: any) {
    setLoading("submit");
    var response = await postRequest(
      `payment_requests/${showModal}`,
      { payment_request_id: selectedRow.id, ...data },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        fetchTableData();
        navigateTo("/payment-request")
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${showModal} payment request.`
      );
    }
  }

  async function deleteExpense() {
    var response = await postRequest(
      `payment_requests/delete`,
      {
        payment_request_id: selectedRow.id,
      },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        setShowModal("");
        clearFields();
        fetchTableData();
      }, 1000);
    } else {
      message.error(
        formatString(
          response?.data?.response?.data?.messages?.error_messages[0] ??
            "Failed to delete payment request."
        )
      );
    }
  }

  async function fetchTableData() {
    setDataSource([]);
    setLoading("fetch");
    var response = await getRequest(
      `payment_requests/get`,
      filterConfig,
      true
    );
    if (response.success) {
      setDataSize(response.data.size);
      setDataSource(response.data.data);
    }
    setLoading("");
  }

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Payment Requests"
              withDateRangeFilter={false}
              withAddButton={true}
              addBtnText="Payment Request"
              onCreate={() => navigateTo("/payment-request/add")}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({
                  ...prev,
                  search: value,
                }))
              }
            />
          </Col>
          <Col xs={24} className="mb-1">
            <span>
              <strong>Filter By:</strong>
            </span>
          </Col>
          <Col xs={24} md={12} lg={8} className="mb-3">
            <RangePicker
              picker="month"
              value={[
                filterConfig.date_from ? dayjs(filterConfig.date_from) : null,
                filterConfig.date_to ? dayjs(filterConfig.date_to) : null,
              ]}
              format="MMMM YYYY" // e.g., "July 2025"
              onChange={(e) => handleFilterChange(e, "dates")}
              allowClear
              className="w-full"
            />
          </Col>
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[800px]">
                <Tabs
                  type="card"
                  className="custom-tabs"
                  activeKey={activeTab}
                  onTabClick={onChangeTab}
                >
                  <TabPane tab="Pending" key="pending">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={loading === "fetch"}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) =>
                          handleChangePage(page, setFilterConfig),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size, setFilterConfig),
                      }}
                      rowClassName={() => "cursor-pointer"}
                      onRow={(record: any) => ({
                        onClick: () => {
                          setSelectedRow(record);
                          form.setFieldsValue(record);
                          // setShowModal("update");
                        },
                      })}
                    />
                  </TabPane>
                  <TabPane tab="Approved" key="approved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={loading === "fetch"}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) =>
                          handleChangePage(page, setFilterConfig),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size, setFilterConfig),
                      }}
                      rowClassName={() => "cursor-pointer"}
                      onRow={(record: any) => ({
                        onClick: () => {
                          setSelectedRow(record);
                          form.setFieldsValue(record);
                          // setShowModal("update");
                        },
                      })}
                    />
                  </TabPane>
                  <TabPane tab="Disapproved" key="disapproved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={loading === "fetch"}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) =>
                          handleChangePage(page, setFilterConfig),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size, setFilterConfig),
                      }}
                      rowClassName={() => "cursor-pointer"}
                      onRow={(record: any) => ({
                        onClick: () => {
                          setSelectedRow(record);
                          form.setFieldsValue(record);
                          // setShowModal("update");
                        },
                      })}
                    />
                  </TabPane>
                </Tabs>
              </div>
            </div>
          </Col>
        </Row>
      </Layout>
      <PersistentFooter />
    </Layout>
  );
};

export default PaymentRequest;
